/** @jsxImportSource @emotion/react */
import React, { useEffect } from 'react';
import { Grid, Box, Button, Typography, Container } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import styled from '@emotion/styled';
import RcStepperHeader from '../StepperHeader';
import { useTranslation } from 'react-i18next';
import { isAuthenticated } from '@/services/utils';
import { useAppSelector } from '@/redux/hooks';
import LanguageSwitcher from '@/components/LanguageSwitcher';

type Props = {
  children: React.ReactNode;
};

const Footer = styled.footer`
  margin-top: 2rem;
  padding: 1rem 2rem;
  text-align: center;
  font-size: 12px;
  color: gray;
`;

const RcLayout: React.FC<Props> = ({ children }) => {
  const { t } = useTranslation('rc');
  const isTempRegistered = useAppSelector(state => state.user.isTempRegistered);
  // L'utilisateur peut avoir un vrai token (déjà connecté) OU venir de finir l'inscription
  // temporaire du step 2 (pas encore de token JWT mais plus besoin du bouton Connexion).
  const isConnected = !!isAuthenticated() || isTempRegistered;

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://js-na1.hs-scripts.com/5696105.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#fefefe' }}>
      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        sx={{ py: 2, px: 3, backgroundColor: '#fff' }}
      >
        <Grid item xs={6} sm={2} textAlign="left">
          <RouterLink to="/">
            <Box
              component="img"
              src="/images/logos/logo-blue.png"
              alt="logo"
              sx={{
                height: { xs: 64, sm: 90 },
                width: { xs: 42, sm: 52 },
                mx: { xs: 'auto', sm: 0 },
              }}
            />
          </RouterLink>
        </Grid>
        <Grid item xs={12} sm={8} textAlign="center">
          <Typography
            variant="h4"
            fontWeight={300}
            sx={{
              textTransform: 'uppercase',
              fontSize: { xs: '18px', sm: '30px' },
              lineHeight: 1.2,
            }}
          >
            {t('layout.title')}
          </Typography>
          <RcStepperHeader />
        </Grid>
        <Grid item xs={6} sm={2} textAlign="right" />
        <Box
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
            p: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <LanguageSwitcher color="#2F6472" />
          {!isConnected && (
            <Button
              variant="contained"
              sx={{
                borderRadius: '20px',
                backgroundColor: '#2F6472',
                '&:hover': {
                  backgroundColor: 'white',
                  color: '#2F6472',
                },
                px: 3,
              }}
              component={RouterLink}
              to="/login"
            >
              {t('layout.login')}
            </Button>
          )}
        </Box>
      </Grid>

      <Container maxWidth="md" sx={{ py: 4 }}>
        {children}
      </Container>

      <Footer>&copy; {new Date().getFullYear()} Studcorp</Footer>
    </Box>
  );
};

export default RcLayout;
